import React, { useRef, useState, useEffect } from "react";
import style from "../style/components/DatePicker.module.css";

const formatDate = (date: Date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

export interface DatePickerProps {
    label: string;
    value: Date;
    onChange: (date: Date) => void;
    disabled?: boolean;
    className?: string;
}

const DatePicker: React.FC<DatePickerProps> = ({ label, value, onChange, disabled = false, className }) => {
    const calendar = useRef<HTMLDivElement>(null);
    const [showCalendar, setShowCalendar] = useState(false);
    const [viewMonth, setViewMonth] = useState(value.getMonth());
    const [viewYear, setViewYear] = useState(value.getFullYear());

    // 월/년 보정
    useEffect(() => {
        if (viewMonth < 0) {
            setViewYear((prev) => prev - 1);
            setViewMonth(11);
        } else if (viewMonth > 11) {
            setViewYear((prev) => prev + 1);
            setViewMonth(0);
        }
    }, [viewMonth]);

    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const firstDay = new Date(viewYear, viewMonth, 1).getDay();

    const handleSelect = (day: number) => {
        const selectedDate = new Date(viewYear, viewMonth, day);
        onChange(selectedDate);
        setShowCalendar(false);
    };

    const isSameDate = (d1: Date, d2: Date) =>
        d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (!calendar.current?.contains(e.target as Node)) {
                if (showCalendar) {
                    setShowCalendar(false);
                }
            }
        };

        if (showCalendar) {
            document.body.addEventListener("click", handleClickOutside);
        }

        return () => {
            document.body.removeEventListener("click", handleClickOutside);
        };
    }, [showCalendar]);

    return (
        <div className={`${style["date-picker"]} ${className || ""}`} ref={calendar}>
            <label className={style["date-label"]}>{label}</label>
            <div
                className={style["date-selected"]}
                onClick={() => !disabled && setShowCalendar(!showCalendar)}
                style={{ cursor: disabled ? "not-allowed" : "pointer" }}
            >
                {formatDate(value)}
            </div>

            {showCalendar && !disabled && (
                <div className={style["calendar"]}>
                    <div className={style["calendar-header"]}>
                        <button className={style["calendar-button"]} onClick={() => setViewMonth(viewMonth - 1)}>
                            ◀
                        </button>
                        <span>
                            {viewYear}년 {viewMonth + 1}월
                        </span>
                        <button className={style["calendar-button"]} onClick={() => setViewMonth(viewMonth + 1)}>
                            ▶
                        </button>
                    </div>
                    <div className={style["calendar-grid"]}>
                        {Array.from({ length: firstDay }).map((_, i) => (
                            <div key={"empty-" + i} />
                        ))}
                        {Array.from({ length: daysInMonth }).map((_, i) => {
                            const day = i + 1;
                            const current = new Date(viewYear, viewMonth, day);
                            const isSelected = isSameDate(current, value);
                            return (
                                <div
                                    key={day}
                                    className={`${style["calendar-day"]} ${
                                        isSelected ? style["calendar-day--selected"] : ""
                                    }`}
                                    onClick={() => handleSelect(day)}
                                >
                                    {day}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DatePicker;
